import { getVisibility } from "@client/lib/settings";
import {
  classNamePropsSelector,
  combinedStyleSelector,
  createShallowEqualSelector,
  getLayoutSettingValueSelector,
  getSettingValueSelector,
  getVisibilitySelector,
  headerElementsColorSelector,
  headerHeightSelector,
  headerStickyElementsColorSelector,
  headerWidthSelector,
  makeBorderStyleSelector,
  makeBoxShadowStyleSelector,
  makePaddingStyleSelector,
  TargetSelector,
  viewportSelector
} from "@client/selectors";
import { type AppState, type ElementVisibility, ScreenType } from "@client/types";
import classnames from "classnames";
import { createSelector, type Selector } from "reselect";

export const headerSelector = (state: AppState) => state.widgets.header;
export const isMenuOpenSelector = (state: AppState) => state.widgets.header.isMenuOpen;
export const isStickySelector = (state: AppState) => state.widgets.header.isSticky;

export const headerStickySelector = createSelector(
  [getLayoutSettingValueSelector("sticky", "header")],
  (sticky) => !!sticky
);

export const stickyCustomLogoSelector = getLayoutSettingValueSelector("customLogo", "header.sticky");
const elementsHeaderSelector = getSettingValueSelector(TargetSelector.Layout, "header", "elements");
export const headerVisibilitySelector = createSelector(
  [elementsHeaderSelector],
  (isVisible): ElementVisibility => getVisibility(isVisible)
);

export const menuPositionSelector = getLayoutSettingValueSelector("position", "header.menu");
export const headerLogoVisibilityPositionSelector = getVisibilitySelector(TargetSelector.Layout);

const headerLogoPositionSelector = getLayoutSettingValueSelector("position", "header.logo");
const headerStickyLogoPositionSelector = getLayoutSettingValueSelector("position", "header.sticky.logo");

export const currentHeaderLogoPositionSelector = createSelector(
  [isStickySelector, stickyCustomLogoSelector, headerLogoPositionSelector, headerStickyLogoPositionSelector],
  (isSticky, stickyCustomLogo, position, stickyPosition) => (isSticky && stickyCustomLogo ? stickyPosition : position)
);

export const headerOverlapSelector = getLayoutSettingValueSelector("overlap", "header");

export const containerStyleSelector = createShallowEqualSelector(
  [headerOverlapSelector, headerStickySelector],
  (overlap, isSticky): React.CSSProperties => ({
    position: isSticky ? "sticky" : "relative",
    overflow: "visible",
    top: 0,
    height: overlap ? 0 : undefined
  })
);

const headerPaddingSelector = makePaddingStyleSelector(TargetSelector.Layout, "header");
export const contentStyleSelector = createShallowEqualSelector(
  [headerHeightSelector, headerWidthSelector, headerPaddingSelector],
  (height, width, padding): React.CSSProperties => ({
    ...padding,
    lineHeight: height,
    height: "auto",
    minHeight: height,
    margin: "0 auto",
    maxWidth: width,
    width: "100%"
  })
);

const stickyAnimationDurationSelector = getLayoutSettingValueSelector("duration", "header.sticky.animation");
const stickyBoxShadowSelector = makeBoxShadowStyleSelector(TargetSelector.Layout, "header.sticky");

const borderSelector = makeBorderStyleSelector(TargetSelector.Layout, "header");

const regularStyleSelector = combinedStyleSelector(TargetSelector.Layout, ["boxShadow", "border"], "header");

const stickyStyleSelector = createShallowEqualSelector(
  [borderSelector, stickyBoxShadowSelector, stickyAnimationDurationSelector],
  (border, boxShadow, animationDuration): React.CSSProperties => ({
    ...border,
    ...boxShadow,
    animation: animationDuration ? `fadeIn ${animationDuration}` : undefined,
    position: "sticky",
    top: 0,
    zIndex: 201
  })
);

export const styleSelector = createSelector(
  [regularStyleSelector, stickyStyleSelector, isStickySelector],
  (regularStyle, stickyStyle, isSticky) => (isSticky ? stickyStyle : regularStyle)
);

export const menuEffectSelector = getLayoutSettingValueSelector("showEffect", "header.menu");
export const isMenuRegular = (menuEffect: string) => menuEffect === "wsc-empt-menu-visible";
export const isMenuOverlay = (menuEffect: string) => menuEffect === "menu-overlay menu-onclick";
export const isMenuSlide = (menuEffect: string) => menuEffect === "menu-slide";

export const elementStyleSelector = createSelector(
  [headerElementsColorSelector, headerStickyElementsColorSelector, isStickySelector],
  (color, stickyColor, isSticky): React.CSSProperties & { "--color": string } => ({
    color: isSticky ? stickyColor ?? color : color,
    fontSize: 13,
    cursor: "pointer",
    "--color": isSticky ? stickyColor ?? color : color
  })
);

const headerElementsShowHideSelector = getLayoutSettingValueSelector("elements", "header");
const getHeaderElementIsVisibleSelector = (element: "adminLogin" | "cart" | "login" | "register" | "search") =>
  createSelector([headerElementsShowHideSelector], (showHide): boolean => (showHide || "").includes(element));

export const isAdminLoginVisibleSelector = getHeaderElementIsVisibleSelector("adminLogin");
export const isCartVisibleSelector = getHeaderElementIsVisibleSelector("cart");
export const isLoginVisibleSelector = getHeaderElementIsVisibleSelector("login");
export const isRegisterVisibleSelector = getHeaderElementIsVisibleSelector("register");
export const isSearchVisibleSelector = getHeaderElementIsVisibleSelector("search");

export const isSearchBarVisibleSelector = createSelector(
  [isSearchVisibleSelector, headerSelector],
  (isSearchVisible, header) => isSearchVisible && header.isSearchBarVisible
);

export const menuVisibilitySelector = getVisibilitySelector(TargetSelector.Layout, "header.menu") as Selector<
  any,
  ElementVisibility
>;

export const isOverlayMenuVisibleSelector = createSelector(
  [menuVisibilitySelector, menuEffectSelector],
  (menuVisibility, menuEffect) => menuVisibility.isVisible && isMenuOverlay(menuEffect)
);

export const isOverlayMenuActiveSelector = createSelector(
  [isOverlayMenuVisibleSelector, headerSelector],
  (isVisible, header) => isVisible && header.isMenuOpen
);

export const classNameSelector = createSelector(
  [
    classNamePropsSelector,
    menuPositionSelector,
    currentHeaderLogoPositionSelector,
    isSearchBarVisibleSelector,
    isMenuOpenSelector,
    menuEffectSelector,
    headerOverlapSelector,
    viewportSelector
  ],
  (className, menuPosition, logoPosition, isSearchBarVisible, isMenuOpen, menuEffect, overlap, viewport) =>
    classnames(className, menuPosition, {
      [`logo-${logoPosition}`]: logoPosition !== "center" || viewport.screen === ScreenType.Desktop,
      "search-active": isSearchBarVisible,
      "menu-overlay-active": isMenuOpen && isMenuOverlay(menuEffect),
      overlap
    })
);
